import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

const DEFAULT_ACCEPTED_MIME_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;

export interface FileUploadProps {
  currentImageUrl?: string | null;
  helperText?: string;
  disabled?: boolean;
  error?: string;
  label?: string;
  showLabel?: boolean;
  /** Default: jpeg, png, webp. Use ["image/jpeg", "image/png"] for JPG/PNG only. */
  acceptedMimeTypes?: string[];
  onFileChange: (file: File | null) => void;
  /** Extra actions rendered beside the replace/select button, so a caller can
   *  put a sibling action on the same row instead of floating one elsewhere. */
  actions?: React.ReactNode;
}

export function FileUpload({
  currentImageUrl,
  helperText,
  disabled,
  error,
  label,
  showLabel = true,
  acceptedMimeTypes = DEFAULT_ACCEPTED_MIME_TYPES,
  onFileChange,
  actions,
}: FileUploadProps): JSX.Element {
  const { t } = useTranslation();
  const hasWebP = acceptedMimeTypes.includes("image/webp");
  const acceptHint = hasWebP
    ? t("components.fileUpload.acceptHintJpgPngWebp")
    : t("components.fileUpload.acceptHintJpgPng");
  const displayLabel = label ?? t("components.fileUpload.defaultLabel");
  const [previewUrl, setPreviewUrl] = useState<string | null>(
    currentImageUrl ?? null
  );
  const [validationError, setValidationError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const blobUrlRef = useRef<string | null>(null);
  const [hasCustomFile, setHasCustomFile] = useState(false);

  useEffect(() => {
    if (!hasCustomFile) {
      setPreviewUrl(currentImageUrl ?? null);
    }
  }, [currentImageUrl, hasCustomFile]);

  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
      }
    };
  }, []);

  const clearBlobUrl = useCallback(() => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  }, []);

  const handleFiles = useCallback(
    (file: File | null) => {
      if (!file) {
        clearBlobUrl();
        setHasCustomFile(false);
        setValidationError(null);
        setPreviewUrl(currentImageUrl ?? null);
        onFileChange(null);
        return;
      }
      if (!acceptedMimeTypes.includes(file.type)) {
        setValidationError(
          acceptedMimeTypes.length === 2
            ? t("components.fileUpload.validationOnlyJpgPng")
            : t("components.fileUpload.validationOnlyJpgPngWebp")
        );
        return;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        setValidationError(t("components.fileUpload.validationMaxSize"));
        return;
      }
      setValidationError(null);
      clearBlobUrl();
      const blobUrl = URL.createObjectURL(file);
      blobUrlRef.current = blobUrl;
      setPreviewUrl(blobUrl);
      setHasCustomFile(true);
      onFileChange(file);
    },
    [clearBlobUrl, currentImageUrl, acceptedMimeTypes, onFileChange, t]
  );

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0] ?? null;
      handleFiles(file);
    },
    [handleFiles]
  );

  const triggerInput = useCallback(() => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  }, [disabled]);

  const shouldRenderLabel = showLabel && displayLabel;

  return (
    <div className="space-y-2">
      {shouldRenderLabel && (
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium">{displayLabel}</Label>
          {helperText != null && (
            <p className="text-xs text-muted-foreground">{helperText}</p>
          )}
        </div>
      )}
      <div
        role="button"
        tabIndex={0}
        onClick={triggerInput}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            triggerInput();
          }
        }}
        className={cn(
          "flex flex-col gap-3 rounded-lg border border-dashed border-border bg-muted/40 px-4 py-4 transition-colors hover:border-primary hover:bg-muted/60 sm:flex-row sm:items-center",
          disabled && "cursor-not-allowed opacity-70"
        )}
      >
        <Avatar className="h-20 w-20 shrink-0 sm:h-14 sm:w-14">
          {previewUrl ? (
            <AvatarImage src={previewUrl} alt="Preview" />
          ) : (
            <AvatarFallback>
              <span className="text-xs uppercase tracking-wide text-muted-foreground">
                {t("components.fileUpload.image")}
              </span>
            </AvatarFallback>
          )}
        </Avatar>
        <div className="min-w-0 flex-1 space-y-0.5">
          <p className="text-sm font-medium text-foreground">
            {previewUrl
              ? t("components.fileUpload.changeImage")
              : t("components.fileUpload.uploadImage")}
          </p>
          <p className="text-xs text-muted-foreground">
            {acceptHint}
          </p>
        </div>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedMimeTypes.join(",")}
        className="sr-only"
        disabled={disabled}
        onChange={handleInputChange}
      />
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={triggerInput}
          disabled={disabled}
        >
          {previewUrl != null
            ? t("components.fileUpload.replace")
            : t("components.fileUpload.selectFile")}
        </Button>
        {actions}
      </div>
      {(validationError != null || error != null) && (
        <p className="text-xs text-destructive">
          {validationError ?? error}
        </p>
      )}
    </div>
  );
}
